import React from "react";

interface AudioScrubBarProps {
  /** playback fraction, 0..1 */
  progress: number;
  /** scrub handler receiving a 0..1 ratio; omit for a display-only (non-interactive) bar */
  onSeek?: (ratio: number) => void;
  /** dim the track + fill, e.g. when the clip is locked or not yet playable */
  muted?: boolean;
  className?: string;
}

// the shared progress track used by both the recorder preview and the in-bubble voice player,
// so they read as one visual language: a brutalist pill with an accent fill. Scrubbable only
// when an onSeek handler is supplied.
export function AudioScrubBar({ progress, onSeek, muted, className }: AudioScrubBarProps) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    onSeek(ratio);
  };

  const pct = Math.min(100, Math.max(0, progress * 100));

  return (
    <div
      className={`relative h-2.5 rounded-full border-2 border-ink overflow-hidden ${
        muted ? "bg-gray-100" : "bg-white"
      } ${onSeek ? "cursor-pointer" : ""} ${className ?? ""}`}
      onClick={handleClick}
      role={onSeek ? "slider" : "presentation"}
      aria-hidden={onSeek ? undefined : true}
      aria-label={onSeek ? "seek" : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round(pct) : undefined}
    >
      <div
        className={`absolute inset-y-0 left-0 transition-[width] duration-150 ease-linear ${
          muted ? "bg-gray-300" : "bg-accent"
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
